import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ClientSelector } from './ClientSelector'
import { Mail, Send } from 'lucide-react'

interface SelectedClient {
  id: string
  email: string
  full_name: string | null
  company: string | null
}

interface CreateAppointmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: {
    clientId?: string
    clientEmail: string
    clientName: string
    dateHeure: string
    description: string
    sendConfirmationEmail: boolean
  }) => Promise<void>
  isSubmitting: boolean
  prefillDate?: string
}

export function CreateAppointmentModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  prefillDate,
}: CreateAppointmentModalProps) {
  const [mode, setMode] = useState<'existing' | 'manual'>('existing')
  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(null)
  const [clientEmail, setClientEmail] = useState('')
  const [clientName, setClientName] = useState('')
  const [dateHeure, setDateHeure] = useState(prefillDate || '')
  const [description, setDescription] = useState('')
  const [sendEmail, setSendEmail] = useState(true)

  // Reset when prefillDate changes
  if (prefillDate && dateHeure !== prefillDate && !dateHeure) {
    setDateHeure(prefillDate)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const email = mode === 'existing' ? selectedClient?.email || '' : clientEmail
    const name = mode === 'existing' ? selectedClient?.full_name || '' : clientName

    if (!email) return

    await onSubmit({
      clientId: mode === 'existing' ? selectedClient?.id : undefined,
      clientEmail: email,
      clientName: name,
      dateHeure,
      description,
      sendConfirmationEmail: sendEmail,
    })

    // Reset form
    setSelectedClient(null)
    setClientEmail('')
    setClientName('')
    setDateHeure('')
    setDescription('')
    setSendEmail(true)
    setMode('existing')
  }

  const handleClose = () => {
    setSelectedClient(null)
    setClientEmail('')
    setClientName('')
    setDateHeure('')
    setDescription('')
    setSendEmail(true)
    setMode('existing')
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nouveau rendez-vous"
      description="Planifier un rendez-vous avec un client"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Toggle mode */}
        <div className="flex gap-2 bg-beige-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'existing'
                ? 'bg-white text-brown-900 shadow-sm'
                : 'text-brown-600 hover:text-brown-900'
            }`}
          >
            Client existant
          </button>
          <button
            type="button"
            onClick={() => setMode('manual')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'manual'
                ? 'bg-white text-brown-900 shadow-sm'
                : 'text-brown-600 hover:text-brown-900'
            }`}
          >
            Nouveau client
          </button>
        </div>

        {/* Client selection */}
        {mode === 'existing' ? (
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Client *
            </label>
            <ClientSelector
              onSelect={setSelectedClient}
              onSwitchToManual={() => setMode('manual')}
              selectedClient={selectedClient}
              onClear={() => setSelectedClient(null)}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Nom du client *
              </label>
              <Input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Jean Dupont"
                required={mode === 'manual'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Email du client *
              </label>
              <Input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="jean@example.com"
                required={mode === 'manual'}
              />
            </div>
          </div>
        )}

        {/* Date/heure */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Date et heure *
          </label>
          <Input
            type="datetime-local"
            value={dateHeure}
            onChange={(e) => setDateHeure(e.target.value)}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
            rows={3}
            placeholder="Motif du rendez-vous..."
          />
        </div>

        {/* Email confirmation checkbox */}
        <label className="flex items-center gap-3 p-3 bg-beige-50 rounded-lg cursor-pointer hover:bg-beige-100 transition">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="w-4 h-4 rounded border-beige-300 text-mint-600 focus:ring-mint-500"
          />
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-brown-500" />
            <span className="text-sm text-brown-700">
              Envoyer un email de confirmation au client
            </span>
          </div>
        </label>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            leftIcon={<Send className="w-4 h-4" />}
            disabled={
              isSubmitting ||
              !dateHeure ||
              (mode === 'existing' && !selectedClient) ||
              (mode === 'manual' && (!clientEmail || !clientName))
            }
          >
            Créer le rendez-vous
          </Button>
        </div>
      </form>
    </Modal>
  )
}
